/**
 * @file page-status.ts
 * @description Статусы страниц и подписи черновика (phase19.16 Р3а; SB126,
 * SB128; uiux-r3 §3.1): «на сайте / правки / скрыта / ещё не была на сайте /
 * удалена», «Страница: 3 правки», «На сайте: версия 7 · 12.10 14:32 · Иван»,
 * «Черновик: 3 правки · Анна, 5 мин назад», плашка «Страницу сейчас правит …».
 *
 * Статусы — реестром (OCP): новый статус — запись в `PAGE_STATUS_META`, а не
 * ветка в списке страниц. Порог «правит другой» — общий с сервером
 * (`isSitePageEditedByOther`, 10 минут), время — параметром (тесты).
 */

import { isSitePageEditedByOther, sitePageChangeCount, summarizePageDiff } from "@chaiberi/shared";

import type { SitePageChangesDto, SitePageDraftState, SitePageLive } from "../api-site-page-publish";
import { pluralRu } from "../plural";
import type { StatusTone } from "../shop/status-meta";
import { formatEditMoment } from "../site-view/changes";
import { formatShopDateTime } from "../shop-time";

/** Статус страницы в списке. */
export type PageStatusKey = "live" | "changes" | "hidden" | "never" | "deleted";

/** Подпись и тон статуса (значок подбирает компонент). */
export const PAGE_STATUS_META: Readonly<Record<PageStatusKey, { label: string; tone: StatusTone }>> = {
  live: { label: "на сайте", tone: "success" },
  changes: { label: "правки", tone: "attention" },
  hidden: { label: "скрыта", tone: "neutral" },
  never: { label: "ещё не была на сайте", tone: "progress" },
  deleted: { label: "удалена", tone: "error" },
};

/** Что о странице знает список (`SitePageListItem`). */
export interface PageStatusInput {
  isPublished: boolean;
  liveVersion?: number | null;
  hasDraftChanges?: boolean;
  deletedAt?: string | null;
}

/**
 * Статус: удалена → ещё не была на сайте (нет версии) → скрыта → правки →
 * на сайте. Поля Р3а нет (старый ответ) — по видимости.
 */
export function pageStatusOf(page: PageStatusInput): PageStatusKey {
  if (page.deletedAt) return "deleted";
  if (page.liveVersion === null) return "never";
  if (!page.isPublished) return "hidden";
  return page.hasDraftChanges ? "changes" : "live";
}

/** Точка «есть правки» в списке: у скрытой страницы тоже (правки ждут публикации). */
export function pageHasDot(page: PageStatusInput): boolean {
  return !page.deletedAt && page.liveVersion !== null && page.hasDraftChanges === true;
}

/** «3 правки». */
export function editsWord(n: number): string {
  return `${n} ${pluralRu(n, ["правка", "правки", "правок"])}`;
}

/** Сколько правок в черновике относительно сайта (блоки, порядок, свойства). */
export function draftEditCount(changes: Pick<SitePageChangesDto, "diff">): number {
  return sitePageChangeCount(summarizePageDiff(changes.diff));
}

/** Пилюля страницы в шапке конструктора. */
export interface PagePill {
  text: string;
  tone: StatusTone;
  /** Есть что открыть (правки) — пилюля со стрелкой «›». */
  edits: number;
}

/** Подписи пилюли по роду: страница или место над/под списком (QA Р3а D11). */
const PILL_WORDS: Readonly<Record<"page" | "slot", { prefix: string; never: string; hidden: string; live: string }>> = {
  page: { prefix: "Страница", never: "Страница: ещё не была на сайте", hidden: "Страница скрыта", live: "Страница на сайте" },
  slot: { prefix: "Место", never: "Место: ещё не было на сайте", hidden: "Место скрыто", live: "Место на сайте" },
};

/** «Страница: 3 правки ›» / «… ещё не была на сайте» / «… скрыта» / «… на сайте»; у места — «Место: …». */
export function pagePillOf(changes: Pick<SitePageChangesDto, "diff" | "hasChanges" | "live" | "isPublished"> & { kind?: string }): PagePill {
  const words = PILL_WORDS[changes.kind === "SLOT" ? "slot" : "page"];
  if (changes.live === null) return { text: words.never, tone: "progress", edits: 0 };
  if (changes.hasChanges) {
    // Пустая сводка при разных отпечатках (правка вне сравниваемых полей) — всё равно «1 правка».
    const edits = Math.max(1, draftEditCount(changes));
    return { text: `${words.prefix}: ${editsWord(edits)}`, tone: "attention", edits };
  }
  return changes.isPublished ? { text: words.live, tone: "success", edits: 0 } : { text: words.hidden, tone: "neutral", edits: 0 };
}

/** Пилюля экрана «Блог»/«Каталог»: правки мест этого экрана («Места: 2 правки»); нет — `null`. */
export function slotsPillOf(slots: ReadonlyArray<Pick<SitePageChangesDto, "diff">>): PagePill | null {
  if (slots.length === 0) return null;
  const edits = slots.reduce((sum, s) => sum + Math.max(1, draftEditCount(s)), 0);
  return { text: `Места: ${editsWord(edits)}`, tone: "attention", edits };
}

/** «5 мин назад», «только что», иначе дата и время по поясу магазина. */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const minutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин назад`;
  return formatEditMoment(iso, now);
}

/** Имя правившего: «вы», если это сам сотрудник. */
export function editorLabel(editor: { id: string; name: string } | null, currentUserId: string | null): string | null {
  if (!editor) return null;
  return editor.id === currentUserId ? "вы" : editor.name;
}

/** «На сайте: версия 7 · 12.10.2026, 14:32 · Иван»; нет версии — «ещё не была на сайте». */
export function liveLine(live: SitePageLive | null): string {
  if (!live) return "Страница ещё не была на сайте — покупатели её не видят";
  const who = live.publishedByName ? ` · ${live.publishedByName}` : "";
  return `На сайте: версия ${live.version} · ${formatShopDateTime(live.publishedAt)}${who}`;
}

/** «Черновик: 3 правки · Анна, 5 мин назад»; правок нет — «Черновик совпадает с сайтом». */
export function draftLine(edits: number, draft: SitePageDraftState, currentUserId: string | null, now: Date = new Date()): string {
  if (edits === 0) return "Черновик совпадает с сайтом";
  const who = editorLabel(draft.editedBy, currentUserId);
  const when = draft.editedAt ? timeAgo(draft.editedAt, now) : "";
  const tail = [who, when].filter(Boolean).join(", ");
  return `Черновик: ${editsWord(edits)}${tail ? ` · ${tail}` : ""}`;
}

/**
 * Плашка «Страницу сейчас правит Анна (5 мин назад)…» (SB126): последняя
 * правка чужая и не старше 10 минут; иначе `null`.
 */
export function editingNoticeText(draft: SitePageDraftState, currentUserId: string | null, now: Date = new Date()): string | null {
  if (!currentUserId || !draft.editedBy) return null;
  if (!isSitePageEditedByOther({ editedById: draft.editedBy.id, editedAt: draft.editedAt }, currentUserId, now)) return null;
  const when = draft.editedAt ? ` (${timeAgo(draft.editedAt, now)})` : "";
  return `Страницу сейчас правит ${draft.editedBy.name}${when}. Ваши правки попадут в тот же черновик — договоритесь, кто публикует.`;
}

/** Подпись версии в истории: заметка сервера («Возврат к версии 3») или сводка правок. */
export function revisionNote(item: { note: string | null; summary: string }): string {
  return item.note?.trim() || item.summary;
}

/** Автор версии: «Система» у переноса, иначе имя; нет имени — «сотрудник». */
export function revisionAuthor(item: { publishedByName: string | null }): string {
  return item.publishedByName?.trim() || "сотрудник";
}

/**
 * Поля статуса по свежей сводке черновика (`GET pages/:id/changes`), если она
 * про эту страницу: список и карточка страницы отстают от правок.
 */
export function withFreshChanges<T extends PageStatusInput & { id: string }>(
  page: T,
  fresh: Pick<SitePageChangesDto, "pageId" | "hasChanges" | "isPublished" | "live"> | null,
): T {
  if (!fresh || fresh.pageId !== page.id) return page;
  return { ...page, isPublished: fresh.isPublished, liveVersion: fresh.live?.version ?? null, hasDraftChanges: fresh.hasChanges && fresh.live !== null };
}
